<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Create New Video</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <VideoForm :channels="sortedChannels" ref="videoForm" />
      <div class="form-actions">
        <button type="submit" class="submit-button big">
          Create Video
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import VideoForm from '@/components/VideoForm.vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { getAllChannels, createChannelVideo, channelKeys } from '@/scripts/api/channelApi';
import { videoKeys } from '@/scripts/api/videoApi';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError } from '@/scripts/shared-utils';

const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();
const videoForm = ref(null); // ref for the child component

// Fetch channels if the user is a superuser
const { data: channels } = useQuery({
  queryKey: channelKeys.getAllChannels(),
  queryFn: getAllChannels,
  enabled: isSuperuser(),
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'Error fetching channels'),
});

const sortedChannels = computed(() => {
  return [...channels.value].sort((a, b) => a.name.localeCompare(b.name));
});

// Mutation for creating the video
const createVideoMutation = useMutation({
  mutationFn: ({ channelId, videoData }) => createChannelVideo(channelId, videoData),
  onSuccess: (data) => {
    // Invalidate channel videos queries
    queryClient.invalidateQueries({ 
      queryKey: channelKeys.getChannelVideos(data.channel_id),
      refetchType: 'all'
    });
    
    // Invalidate all videos query if it exists
    queryClient.invalidateQueries({ 
      queryKey: videoKeys.getAllVideos() 
    });
    
    // On success, navigate away
    router.push(isSuperuser() ? '/admin/publishing' : '/publishing');
  },
  onError: (error) => {
    handleApiError(error, 'Error creating video');
  },
});

// Handle form submission by getting data from the child component
const handleSubmit = () => {
  if (!videoForm.value) return;

  const formData = videoForm.value.formData;
  const channelId = formData.channel_id;

  if (!channelId) {
    alert('Please select a channel.');
    return;
  }

  const videoData = {
    title: formData.title,
    description: formData.description,
  };

  createVideoMutation.mutate({ channelId, videoData });
};

// Handle the cancel button
const handleCancel = () => {
  router.back();
};

// Watch for the child component to be mounted, then set channel_id from route params if available
watch(videoForm, (newVideoForm) => {
  if (newVideoForm && route.params.channel_id) {
    newVideoForm.formData.channel_id = route.params.channel_id;
  }
}, { immediate: true });
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>